import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface BaseOption {
  id: string;
}

type CommonProps<TOption extends BaseOption> = {
  className?: string;
  options: ReadonlyArray<TOption>;
  getOptionLabel: (option: TOption) => string;
  placeholder?: string;
  ariaLabel?: string;
  searchInputAriaLabel?: string;
  inputValue: string;
  onInputValueChange: (value: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
  createOptionLabel?: string;
  hideSelectedOptions?: boolean;
  onCreate?: (value: string) => void;
  isCreating?: boolean;
};

type SingleProps<TOption extends BaseOption> = {
  mode?: 'single';
  value: TOption | null;
  onChange: (value: TOption | null) => void;
  onClear?: () => void;
};

type MultipleProps<TOption extends BaseOption> = {
  mode: 'multiple';
  values: TOption[];
  onChange: (values: TOption[]) => void;
};

type AutocompleteSelectProps<TOption extends BaseOption> = CommonProps<TOption> & (SingleProps<TOption> | MultipleProps<TOption>);

function AutocompleteSelect<TOption extends BaseOption>(props: AutocompleteSelectProps<TOption>) {
  const {
    className,
    options,
    getOptionLabel,
    placeholder = 'Seleccioná una opción',
    ariaLabel,
    searchInputAriaLabel,
    inputValue,
    onInputValueChange,
    disabled,
    isLoading,
    emptyMessage = 'No se encontraron resultados.',
    createOptionLabel,
    onCreate,
    isCreating,
  } = props;

  const multiple = props.mode === 'multiple';
  const selectedItems = multiple
    ? props.values
    : props.value
      ? [props.value]
      : [];
  const hideSelectedOptions = props.hideSelectedOptions ?? false;

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handleSelect = (option: TOption) => {
    if (multiple) {
      const exists = selectedItems.some((item) => item.id === option.id);
      const next = exists
        ? selectedItems.filter((item) => item.id !== option.id)
        : [...selectedItems, option];
      props.onChange(next);
      onInputValueChange('');
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      return;
    }

    props.onChange(option);
    setOpen(false);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (multiple) {
      props.onChange([]);
    } else {
      if ('onClear' in props && props.onClear) {
        props.onClear();
      }
      props.onChange(null);
    }
    onInputValueChange('');
    setOpen(false);
  };

  const handleRemoveSelected = (id: string) => {
    if (!multiple) return;
    props.onChange(selectedItems.filter((item) => item.id !== id));
  };

  const renderedOptions = useMemo(() => {
    const unique = new Map<string, TOption>();

    options.forEach((option) => {
      const isSelected = selectedItems.some((item) => item.id === option.id);
      if (hideSelectedOptions && isSelected) {
        return;
      }
      if (!unique.has(option.id)) {
        unique.set(option.id, option);
      }
    });

    if (!hideSelectedOptions && multiple) {
      selectedItems.forEach((item) => {
        if (!unique.has(item.id)) {
          unique.set(item.id, item);
        }
      });
    }

    return Array.from(unique.values()).map((option) => ({
      option,
      label: getOptionLabel(option),
      isSelected: selectedItems.some((item) => item.id === option.id),
    }));
  }, [options, selectedItems, getOptionLabel, multiple, hideSelectedOptions]);

  const buttonLabel = useMemo(() => {
    if (selectedItems.length === 0) {
      return placeholder;
    }

    if (!multiple) {
      return getOptionLabel(selectedItems[0]);
    }

    if (selectedItems.length <= 2) {
      return selectedItems.map(getOptionLabel).join(', ');
    }

    const firstTwo = selectedItems.slice(0, 2).map(getOptionLabel).join(', ');
    const remaining = selectedItems.length - 2;
    return `${firstTwo} +${remaining}`;
  }, [selectedItems, multiple, getOptionLabel, placeholder]);

  return (
    <div ref={containerRef} className={cn('w-full min-w-0 space-y-2', className)}>
      <div className="relative min-w-0">
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-label={ariaLabel ?? placeholder}
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn(
            'm-0 flex w-full min-w-0 max-w-full !shrink justify-between overflow-hidden text-left',
            selectedItems.length === 0 && 'text-muted-foreground',
            selectedItems.length > 0 && 'pr-16'
          )}
          disabled={disabled}
          onClick={handleToggle}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'Enter') {
              event.preventDefault();
              setOpen(true);
            }
          }}
        >
          <span className="min-w-0 flex-1 truncate pr-2">{buttonLabel}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>

        {selectedItems.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-9 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground transition hover:text-foreground"
            aria-label="Limpiar selección"
            disabled={disabled}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {multiple && selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedItems.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700"
            >
              {getOptionLabel(item)}
              <button
                type="button"
                onClick={() => handleRemoveSelected(item.id)}
                className="text-gray-500 transition hover:text-gray-700"
                aria-label={`Quitar ${getOptionLabel(item)}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="relative z-50">
          <div className="absolute w-full rounded-md border border-gray-200 bg-white shadow-lg">
            <div className="p-2">
              <Input
                ref={inputRef}
                aria-label={searchInputAriaLabel ?? `Buscar en ${ariaLabel ?? placeholder.toLowerCase()}`}
                placeholder="Buscar…"
                value={inputValue}
                onChange={(event) => onInputValueChange(event.target.value)}
                className="h-9"
              />
            </div>

            <div className="max-h-60 overflow-y-auto border-t border-gray-100">
              {isLoading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Buscando…
                </div>
              ) : renderedOptions.length > 0 ? (
                renderedOptions.map(({ option, label, isSelected }) => (
                  <button
                    key={option.id}
                    type="button"
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition hover:bg-gray-50',
                      isSelected && 'bg-gray-50'
                    )}
                    onClick={() => handleSelect(option)}
                  >
                    <span className="truncate">{label}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                  </button>
                ))
              ) : (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>
              )}

              {onCreate && createOptionLabel && (
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 border-t border-gray-100 bg-gray-50 px-3 py-2 text-left text-sm font-medium text-primary hover:bg-primary/5"
                  onClick={() => {
                    const trimmed = inputValue.trim();
                    if (!trimmed) return;
                    onCreate(trimmed);
                  }}
                  disabled={isCreating}
                >
                  {createOptionLabel}
                  {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { AutocompleteSelect };
